import { SECTION_FIELDS, type FieldType } from './model';

/**
 * E34 · Worksheets, block 2b — the PURE part of the client hub's "Worksheets & documents" tab, the
 * response page and "Fill with the client": the reads' field lists, the row and the answer types, the
 * filters, who may be filled with, an answer as the page writes it, and the in-session draft → the wire.
 * No i18n here: helpers return keys or data, the screens translate.
 */

export const HUB_FILTERS = [
  'all',
  'pending',
  'sessions',
  'problems',
  'selfEsteem',
  'emotions',
  'relationships',
  'intake',
  'consent',
  'homework',
] as const;
/** F-197: `homework` is history only — its chip shows only while this client has a row filed under it. */
export const HUB_LEGACY_FILTERS: readonly HubFilter[] = ['homework'];
export type HubFilter = (typeof HUB_FILTERS)[number];

export const CLIENT_WORKSHEET_FIELDS = [
  'assignmentId',
  'subject',
  'templateId',
  'templateVersion',
  'name',
  'category',
  'status',
  'awaitingSignature',
  'pending',
  'sentAt',
  'viewedAt',
  'submittedAt',
  'signedAt',
  'submissionId',
  'submissionIds',
  'filledInSession',
  'score',
  'selfAssigned',
  { paperPhoto: ['fileRef', 'takenAt'] }, // Q-paper-2 A: a worksheet done on paper — the photo of the filled sheet
];

export type ClientWorksheetStatus =
  | 'sent'
  | 'viewed'
  | 'submitted'
  | 'signed'
  | 'paper' // Q-paper-2 A: completed on paper — no response to open, a photo instead
  | 'cancelled';

export type ClientWorksheetRow = {
  assignmentId: string;
  subject: string;
  templateId: string;
  templateVersion: number | null;
  name: string | null;
  category: string | null;
  status: ClientWorksheetStatus;
  awaitingSignature: boolean;
  pending: boolean;
  sentAt: string;
  viewedAt: string | null;
  submittedAt: string | null;
  signedAt: string | null;
  submissionId: string | null;
  submissionIds: string[];
  filledInSession: boolean;
  score: number | null;
  selfAssigned: boolean;
  paperPhoto?: { fileRef: string; takenAt: string } | null;
};

/**
 * "Fill with the client": the practice's own document that has not been sent (or the practice reopened),
 * never a client's self-set journal (theirs alone) and never a withdrawn one. A form already sent is
 * opened for its response instead.
 */
export const canFillWithClient = (
  row: Pick<ClientWorksheetRow, 'status' | 'selfAssigned'>,
): boolean => !row.selfAssigned && (row.status === 'sent' || row.status === 'viewed');

export const canOpenResponse = (
  row: Pick<ClientWorksheetRow, 'submissionId'>,
): boolean => !!row.submissionId;

// ── the response page ──────────────────────────────────────────────────────────────────────────

export const RESPONSE_FIELDS = [
  'submissionId',
  'assignmentId',
  'subject',
  'templateId',
  'templateVersion',
  'name',
  { sections: SECTION_FIELDS },
  { scoring: ['kind', 'fields', { bands: ['min', 'max', 'label'] }] },
  'answers',
  'performer',
  'performerName',
  'signatureRequired',
  'signerName',
  'submittedAt',
  'signedAt',
  'score',
  'band',
  'filledInSession',
];

export type WorksheetFieldData = {
  fieldId: string;
  type: FieldType;
  label: string;
  required?: boolean | null;
  options?: string[] | null;
  min?: number | null;
  max?: number | null;
};

export type WorksheetSectionData = {
  sectionId: string;
  title?: string | null;
  text?: string | null;
  video?: { source: string; ref: string; title: string; duration?: number | null } | null;
  fields: WorksheetFieldData[];
};

export type WorksheetAnswerValue = string | number | boolean | string[] | null;

export type WorksheetResponseData = {
  submissionId: string;
  assignmentId: string;
  subject: string;
  templateId: string;
  templateVersion: number;
  name: string | null;
  sections: WorksheetSectionData[];
  scoring: { kind: string; fields: string[]; bands: Array<{ min: number; max: number; label: string }> } | null;
  answers: Record<string, WorksheetAnswerValue>;
  performer: string;
  performerName: string | null;
  signatureRequired: boolean;
  signerName: string | null;
  submittedAt: string;
  signedAt: string | null;
  score: number | null;
  band: string | null;
  filledInSession: boolean;
};

/** An answer as the response page writes it; null when the question was left unanswered (the page says so). */
export const answerText = (
  field: Pick<WorksheetFieldData, 'type'>,
  value: WorksheetAnswerValue | undefined,
  yes: string,
  no: string,
  formatDate: (iso: string) => string,
): string | null => {
  if (value === undefined || value === null) return null;
  if (field.type === 'checkbox') return value === true ? yes : no;
  if (Array.isArray(value)) return value.length ? value.join(', ') : null;
  if (typeof value === 'string') {
    if (!value.trim()) return null;
    return field.type === 'date' ? formatDate(value) : value;
  }
  return String(value);
};

// ── "Fill with the client" ─────────────────────────────────────────────────────────────────────

export const FORM_FIELDS = [
  'assignmentId',
  'subject',
  'templateId',
  'templateVersion',
  'name',
  'category',
  'language',
  { sections: SECTION_FIELDS },
  'signatureRequired',
  'repeatable',
  'submittedAt',
  'submissionId',
  'awaitingSignature',
  'signedAt',
  'cancelled',
];

export type WorksheetFormData = {
  assignmentId: string;
  subject: string;
  templateId: string;
  templateVersion: number;
  name: string;
  category: string;
  language: string;
  sections: WorksheetSectionData[];
  signatureRequired: boolean;
  repeatable: boolean;
  submittedAt: string | null;
  submissionId: string | null;
  awaitingSignature: boolean;
  signedAt: string | null;
  cancelled: boolean;
};

/** What the therapist has typed: a number / scale is its text, a several-choice its ticked options. */
export type FillDraft = Record<string, string | boolean | string[]>;

/**
 * The draft → the wire's `answers`: an untouched optional question is left out, a tick is always its
 * boolean, a number is a number. Only the version's own fields are sent (WORKSHEET_UNKNOWN_FIELD).
 */
export const fillAnswers = (
  sections: readonly WorksheetSectionData[],
  draft: FillDraft,
): Record<string, string | number | boolean | string[]> => {
  const out: Record<string, string | number | boolean | string[]> = {};
  for (const f of sections.flatMap((s) => s.fields)) {
    const v = draft[f.fieldId];
    switch (f.type) {
      case 'checkbox':
        out[f.fieldId] = v === true;
        break;
      case 'number':
      case 'scale': {
        if (typeof v !== 'string' || !v.trim()) break;
        const n = Number(v.trim().replace(',', '.'));
        if (Number.isFinite(n)) out[f.fieldId] = n;
        break;
      }
      case 'multiChoice':
        if (Array.isArray(v) && v.length) {
          out[f.fieldId] = (f.options ?? []).filter((o) => v.includes(o));
        }
        break;
      default:
        if (typeof v === 'string' && v.trim()) out[f.fieldId] = v.trim();
    }
  }
  return out;
};

/** The scale's whole steps, or null when it is too wide for buttons (a plain number field then). */
export const scaleSteps = (f: Pick<WorksheetFieldData, 'min' | 'max'>): number[] | null => {
  const min = f.min ?? 0;
  const max = f.max ?? 10;
  if (!Number.isInteger(min) || !Number.isInteger(max) || max < min || max - min + 1 > 21) {
    return null;
  }
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
};
